import {
  NgModule,
  ModuleWithProviders,
  APP_INITIALIZER,
  Injector
} from '@angular/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';

import { OidcConfigService } from './open-idconnect/services/oidc-config.service';
import { AppLoadService } from './app-loader.service';
import { AuthGuardService } from './services/auth-guard.service';
import { AuthenticationService } from './services/authentication.service';
import { StorageService } from './services/storage.service';
import { OidcAuthenticationService } from './open-idconnect/services/oidc-authentication.service';
import { OAuthInterceptor, oauth2Interceptor } from './interceptors/oauth-interceptor';
import { CookieStorageService } from './services/cookie-storage.service';
import { OidcLoaderService } from './open-idconnect/oidc-loader.service';
import { User } from './user';
import { OpenIDConnectModule } from './open-idconnect/open-idconnect.module';
import { UserManagementService } from './services/user-management.sevice';
import { RedirectService } from './services/redirect.service';
import { SessionStorageService } from './services/session-storage.service';
import { SecurityConfigWrapper } from './configuration/security.config';
import { AbstractSecurityStorage, authInterceptor  } from 'angular-auth-oidc-client';
import { provideHttpClient, withInterceptors } from '@angular/common/http';

export function preInitialize(loader: AppLoadService) {
  // We need to do something other than the lambda, otherwise
  // ng build will complain stating that lambdas are not supported
  console.debug('Executing preInitialize');
  return () => loader.preInitialize();
}

export function initialize(loader: AppLoadService) {
  // We need to do something other than the lambda, otherwise
  // ng build will complain stating that lambdas are not supported
  console.debug('Executing initialize');
  return () => loader.initialize();
}

export function postInitialize(loader: AppLoadService) {
  // We need to do something other than the lambda, otherwise
  // ng build will complain stating that lambdas are not supported
  console.debug('Executing postInitialize');
  return () => loader.postInitialize();
}

export function resolveUser(authService: AuthenticationService) {
  return authService.user;
}

export function resolveUserManager() {
  // We use the factory to do the injection to prevent a singleton injection
  return new UserManagementService();
}

export function resolveStorageService(configWrapper: SecurityConfigWrapper) {
  let storageService: StorageService;
  const config = configWrapper.configuration;

  // SECDEV-453 For some reason ITP is disabling cookies
  // even when we white-list, this is causing issues with
  // redirect urls among other problems in pure WebKit deployments
  if (isIntelligentTrackingProtectionEnabled()) {
    storageService = new SessionStorageService();
  } else {
    storageService = new CookieStorageService();
  }
  // We concatenate the client id to ensure that the values are unique
  storageService.postfix = '_' + config.configuration.clientId;
  return storageService;
}

export function resolveComponentAuthenticationService() {
  return (<any>window).authenticationService;
}

export function resolveComponentUser() {
  return (<any>window).authenticationService.user;
}

export function resolveSecurityStorage(configWrapper: SecurityConfigWrapper) {
  const config = configWrapper.configuration;
  let storageService: StorageService;

  // OIDC Version 15 upgrade: isInternetExplorer() and CookieStorageService object are removed as
  // this fails because the cookie size eventually gets larger than 4096 bytes so it no longer updates the value in the cookie storage,
  // causing the authentication flow to fail.
  // In previous versions ( <= 9), this wasn't an issue as values were stored as separate cookies rather than 1 "large" json object

  storageService = new SessionStorageService();

  // We concatenate the client id to ensure that the values are unique
  storageService.postfix = '_' + config.configuration.clientId;

  return storageService;
}

function isIntelligentTrackingProtectionEnabled(): boolean {
  const doc: any = document;
  //We need to use an any variable because angular does not recognize the property
  return !!doc.hasStorageAccess;
}

@NgModule()
export class CsxSecurityModule {
  static initialize(): ModuleWithProviders<OpenIDConnectModule> {
    // Angular is very particular about what can be done in the initializer
    // adding any additional logic in here causes the --prod builds to fail spectacularly
    // OIDC Version 15 upgrade: shell app needs routes. so, loading them here
    OpenIDConnectModule.prototype.initRoutes(false);
    return {
      ngModule: OpenIDConnectModule,
      providers: [
        AuthGuardService,
        OidcConfigService,
        RedirectService,
        SecurityConfigWrapper,
        {
          provide: User,
          useFactory: resolveUser,
          deps: [AuthenticationService]
        },
        { provide: AuthenticationService, useClass: OidcAuthenticationService },
        provideHttpClient(withInterceptors([oauth2Interceptor()])),
        {
          provide: StorageService,
          useFactory: resolveStorageService,
          deps: [SecurityConfigWrapper]
        },
        { provide: AppLoadService, useClass: OidcLoaderService },
        { provide: UserManagementService, useFactory: resolveUserManager },
        {
          // OidcSecurityStorage has been replaced by AbstractSecurityStorage in oidc version 15
          provide: AbstractSecurityStorage,
          useFactory: resolveSecurityStorage,
          deps: [SecurityConfigWrapper]
        },
        {
          provide: APP_INITIALIZER,
          useFactory: preInitialize,
          deps: [AppLoadService],
          multi: true
        },
        {
          provide: APP_INITIALIZER,
          useFactory: initialize,
          deps: [AppLoadService],
          multi: true
        },
        {
          provide: APP_INITIALIZER,
          useFactory: postInitialize,
          deps: [AppLoadService],
          multi: true
        }
      ]
    };
  }

  static componentInitialize(): ModuleWithProviders<OpenIDConnectModule> {
    // OIDC Version 15 upgrade: web components do not needs routes. so, not loading them
    OpenIDConnectModule.prototype.initRoutes(true);
    return {
      ngModule: OpenIDConnectModule,
      providers: [
      OidcAuthenticationService,
      AuthenticationService,
        AuthGuardService,
        {
          provide: AuthenticationService,
          useFactory: resolveComponentAuthenticationService
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: OAuthInterceptor,
          multi: true,
          deps: [Injector, AuthenticationService]
        },
        {
          provide: User,
          useFactory: resolveComponentUser,
          deps: [AuthenticationService]
        }
      ]
    };
  }
}
